import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import useFetchData from '../../../../../../../hooks/useFetchData';
import { useDispatch, useSelector } from 'react-redux';
import { updateFilter, showFilter } from '../../../../../../../store/actions/filter.actions';
import { useLocation, useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { updateSkip } from '../../../../../../../store/actions/datatable.actions';
import DialogConfirmCancel from '../../../../../../dialog/DialogConfirm';
import StudentInformation from './StudentInformation';
import CurriculumInformation from './CurriculumInformation';
import { getDevelopmentalRubrics } from '../../../../../../../services/developmental-rubrics/developmental-rubrics.service';
import { getClasses } from '../../../../../../../services/classes/classes.service';
import { useGroupStudentsContext } from '../../GroupStudentsContext/GroupStudentsContext';
import { getFilterCurriculumElementsDR, setChildrenData } from '../../../../../Assessment/utils';

const FilterDialog = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { curriculumId } = useGroupStudentsContext();

  const filter = useSelector((state) => state?.filter.groupStudents?.filter || {});
  const open = useSelector((state) => state?.filter.groupStudents?.open || false);

  const { isLoading: isLoadingYears, data: dataYears, error: errorFetchYears } = useFetchData('setting/year');

  const dispatch = useDispatch();

  const [form, setInputForm] = useState(filter);
  const [error, setInputError] = useState({});

  const [classes, setClasses] = useState({
    data: [],
    loading: false
  });
  const [ul, setUl] = useState({
    data: [],
    loading: false
  });

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter);
    }
  }, [filter]);

  const onClose = () => {
    dispatch(showFilter('groupStudents', false));
  };

  const onChange = (field, key, name, value) => {
    if (field === 'students') {
      if (name === 'yearLevelId') {
        setInputError({ ...error, [name]: null, yearLevel: null });
        const yearLevel = dataYears.filter((item) => value.includes(item._id));
        setInputForm({ ...form, [name]: value, yearLevel });
      } else {
        setInputForm({ ...form, [name]: value });
        setInputError({ ...error, [name]: null });
      }
    } else {
      if (['startDate', 'endDate'].includes(field) && value) {
        setInputForm({ ...form, [field]: value });
      } else if (field === 'ulId') {
        const uls = ul?.data
          ?.filter((cl) => value.includes(cl._id))
          .map((cl) => ({
            name: cl.name,
            value: cl._id
          }));

        setInputForm({ ...form, [field]: value, 'unit of learning': uls });
      } else if (field === 'classId') {
        const cls = classes?.data
          ?.filter((cl) => value.includes(cl._id))
          .map((cl) => ({
            name: cl.code,
            value: cl._id
          }));

        setInputForm({ ...form, [field]: value, classes: cls, ulId: null, 'unit of learning': [] });
      } else if (field === 'curriculumElements') {
        const formData = setChildrenData(form[field] || {}, key, value, true);
        setInputForm({ ...form, [field]: formData });
      } else if (field === 'curriculumElementsClass') {
      } else if (field === 'isActived') {
        setInputForm({ ...form, [name]: value ? 'yes' : 'no' });
      } else {
        const formData = form[field] || {};
        const formDataName = form[`${field}_name`] || {};
        if (formData) {
          if (value) {
            formData[key] = value;
            formDataName[key] = {
              name,
              value
            };
          } else {
            delete formData[key];
            delete formDataName[key];
          }
        }
        setInputForm({ ...form, [field]: formData, [`${field}_name`]: formDataName });
      }
    }
  };

  const onLoadClasses = useCallback(async () => {
    if (curriculumId) {
      let data = classes.data || [];
      setClasses({ data, loading: true });
      try {
        const filter = getFilterCurriculumElementsDR(form.curriculumElements || {});
        const response = await getClasses(curriculumId, {
          curriculumElements: form.curriculumElements,
          yearLevel: form.yearLevelId
        });
        if (!response.data || response.error) {
          console.error(response.error);
          return;
        }
        data = response.data;
      } catch (err) {
        const message = handlerErrorV2(err);
        setInputError((prevError) => ({ ['classId']: message, ...prevError }));
      } finally {
        if (!data.some((cl) => cl._id === 'main')) {
          data.unshift({
            code: 'Main',
            _id: 'main'
          });
        }
        setClasses({ data, loading: false });
      }
    }
  }, [curriculumId, form]);

  const onLoadUl = useCallback(async () => {
    if (curriculumId) {
      let data = ul.data || [];
      let error = null;
      setUl({ data, loading: true, error });
      try {
        const response = await getDevelopmentalRubrics(curriculumId, {
          classId: form.classId
        });
        if (!response.data || response.error) {
          error = response.error;
          return;
        }
        data = response.data;
      } catch (err) {
        const message = handlerErrorV2(err);
        setInputError((prevError) => ({ ['ulId']: message, ...prevError }));
      } finally {
        if (!data.some((cl) => cl._id === 'main')) {
          data.unshift({
            name: 'Main',
            _id: 'main'
          });
        }
        setUl({ data, loading: false, error });
      }
    }
  }, [curriculumId, form.classId, form]);

  const onApply = () => {
    onClose();
    const filter = {
      ...form
    };

    dispatch(updateFilter('groupStudents', filter));
    dispatch(updateSkip('groupStudents', 0));
    navigate(pathname, { state: filter });
  };

  useEffect(() => {
    onLoadClasses();
  }, [onLoadClasses]);
  useEffect(() => {
    onLoadUl();
  }, [onLoadUl]);

  return (
    <DialogConfirmCancel
      id='dialog-filter-users-class-student-selector'
      open={open}
      title='Filter'
      maxWidth='md'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='grid grid-cols-2  gap-4'>
        <StudentInformation
          form={form}
          error={error}
          onChange={(name, value) => onChange('students', name, name, value)}
          isLoadingYears={isLoadingYears}
          dataYears={dataYears}
          errorFetchYears={errorFetchYears}
        />
        <CurriculumInformation form={form} error={error} onChange={onChange} classes={classes} ul={ul} />
      </Box>
    </DialogConfirmCancel>
  );
};

export default FilterDialog;
